import { milliseconds } from "date-fns";
import type { CloseEvent } from "reconnecting-websocket";
import ReconnectingWebSocket from "reconnecting-websocket";
import { store } from "../redux/store";
import {
  updateSocketCloseThunk,
  updateSocketConnectionStatusThunk,
} from "./socketThunk";
import {
  CloseEventReason,
  SocketStatus,
  WebSocketCloseCodes,
} from "./webSocketStatus";

type SocketConfig = {
  url: string;
  debug?: boolean;
  onOpen?: (socket: WebSocket) => void;
  onClose?: (event: CloseEvent) => void;
  onMessage: (args: MessageEvent) => void;
  onError: (event: ErrorEvent) => void;
};

export class SocketClient {
  static #instance: SocketClient | null = null;
  #socket: ReconnectingWebSocket | null = null;
  #config: SocketConfig | null = null;

  private constructor() {}

  static getInstance(): SocketClient {
    if (!SocketClient.#instance) {
      SocketClient.#instance = new SocketClient();
    }
    return SocketClient.#instance;
  }

  initialize = (config: SocketConfig) => {
    this.#config = config;
    return this;
  };

  connect = () => {
    if (this.#socket) {
      return;
    }

    const socket = this.#createSocket();

    this.#socket = socket;
    this.#bindSocketEvents(this.#socket);
  };

  disconnect = () => {
    if (this.#socket) {
      this.#socket.close(WebSocketCloseCodes.CLOSE_NORMAL);
      this.#socket = null;
      store.dispatch(updateSocketConnectionStatusThunk(SocketStatus.CLOSED));
    }
  };

  getWebSocket = () => {
    if (!this.#socket) {
      throw new Error("SocketClient.getWebSocket: Socket is not connected");
    }

    return this.#socket as WebSocket;
  };

  #createSocket = () => {
    if (!this.#config) {
      throw new Error(
        "SocketClient.createSocket: Socket client must be initialized with config first",
      );
    }

    return new ReconnectingWebSocket(this.#config.url, undefined, {
      maxRetries: 5,
      minReconnectionDelay: milliseconds({ seconds: 1 }),
      reconnectionDelayGrowFactor: 1.5,
      maxEnqueuedMessages: 100,
      debug: this.#config.debug ?? false,
    });
  };

  #bindSocketEvents = (ws: ReconnectingWebSocket) => {
    ws.addEventListener("open", () => {
      store.dispatch(updateSocketConnectionStatusThunk(SocketStatus.OPEN));
      this.#config?.onOpen?.(this.getWebSocket());
    });

    ws.addEventListener("close", (event) => {
      if (event.code === WebSocketCloseCodes.SERVICE_RESTART) {
        this.#socket?.reconnect(event.code, event.reason);
        store.dispatch(
          updateSocketCloseThunk(CloseEventReason.SERVICE_RESTART),
        );
        return;
      }

      if (
        event.code === WebSocketCloseCodes.CLOSE_PROTOCOL_ERROR &&
        event.reason === "unauthorized"
      ) {
        store.dispatch(updateSocketCloseThunk(CloseEventReason.UNAUTHORIZED));
        this.#config?.onClose?.(event);
        return;
      }

      updateSocketConnectionStatusThunk(SocketStatus.CONNECTING);
    });

    ws.addEventListener("message", (event) => {
      if (!this.#config) {
        throw new Error(
          "SocketClient.onmessage: Socket client must be initialized with config first",
        );
      }

      this.#config.onMessage(event);
    });

    ws.onerror = (event) => {
      if (!this.#config) {
        throw new Error(
          "SocketClient.onerror: Socket client must be initialized with config first",
        );
      }

      this.#config.onError(event as ErrorEvent);
    };
  };
}
